import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const Sidebar = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const isAuthenticated = !location.pathname.includes('/login') && !location.pathname.includes('/register');

  const navigationItems = [
    { 
      label: 'Dashboard', 
      path: '/dashboard', 
      icon: 'BarChart3',
      description: 'Portfolio overview and insights'
    },
    { 
      label: 'Portfolio', 
      path: '/portfolio-management', 
      icon: 'PieChart',
      description: 'Manage your investments'
    },
    { 
      label: 'Stock Discovery', 
      path: '/stock-search-discovery', 
      icon: 'Search',
      description: 'Find new opportunities'
    },
    { 
      label: 'Account Settings', 
      path: '/account-settings', 
      icon: 'Settings',
      description: 'Manage your account'
    }
  ];

  const quickActions = [
    { label: 'Buy Stock', icon: 'Plus', action: 'buy' },
    { label: 'Sell Stock', icon: 'Minus', action: 'sell' },
    { label: 'View Reports', icon: 'FileText', action: 'reports' },
    { label: 'Market News', icon: 'Newspaper', action: 'news' }
  ];

  const handleNavigation = (path) => {
    navigate(path);
    setIsMobileOpen(false);
  };

  const handleQuickAction = (action) => {
    console.log(`Quick action: ${action}`);
    // Implement quick action logic here
  };

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  const toggleMobile = () => {
    setIsMobileOpen(!isMobileOpen);
  };

  // Close mobile sidebar when route changes
  useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname]);

  // Disable body scroll when sidebar is open on mobile
  useEffect(() => {
    if (isMobileOpen) {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }
    // Cleanup on unmount
    return () => document.body.classList.remove('overflow-hidden');
  }, [isMobileOpen]);

  // Don't render sidebar on auth pages
  if (!isAuthenticated) {
    return null;
  }

  return (
    <>
      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-dropdown lg:hidden"
          onClick={toggleMobile}
        />
      )}

      {/* Mobile Toggle Button */}
      <Button
        variant="ghost"
        size="icon"
        className="fixed top-20 left-4 z-navigation lg:hidden"
        onClick={toggleMobile}
      >
        <Icon name="Menu" size={20} />
      </Button>

      {/* Sidebar */}
      <aside className={`
        fixed top-16 left-0 h-[calc(100vh-4rem)] bg-card border-r border-border z-navigation
        transition-all duration-300 ease-out
        ${isCollapsed ? 'w-16' : 'w-64'}
        ${isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        lg:fixed
      `}>
        <div className="flex flex-col h-full">
          {/* Sidebar Header */}
          <div className="flex items-center justify-between p-4 border-b border-border">
            {!isCollapsed && (
              <h2 className="text-lg font-semibold text-foreground">Navigation</h2>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleCollapse}
              className="hidden lg:flex"
            >
              <Icon name={isCollapsed ? "ChevronRight" : "ChevronLeft"} size={16} />
            </Button>
          </div>

          {/* Navigation Items */}
          <nav className="flex-1 p-4 space-y-2">
            {navigationItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <button
                  key={item.path}
                  onClick={() => handleNavigation(item.path)}
                  className={`
                    w-full flex items-center space-x-3 px-3 py-3 rounded-lg
                    transition-all duration-200 ease-out
                    hover:bg-muted hover:text-foreground
                    focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2
                    ${isActive ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'}
                    ${isCollapsed ? 'justify-center' : 'justify-start'}
                  `}
                  title={isCollapsed ? item.label : ''}
                >
                  <Icon name={item.icon} size={20} />
                  {!isCollapsed && (
                    <div className="flex-1 text-left">
                      <div className="text-sm font-medium">{item.label}</div>
                      <div className="text-xs opacity-75">{item.description}</div>
                    </div>
                  )}
                  {!isCollapsed && isActive && (
                    <div className="w-1 h-6 bg-primary-foreground rounded-full" />
                  )}
                </button>
              );
            })}
          </nav>

          {/* Quick Actions */}
          {!isCollapsed && (
            <div className="p-4 border-t border-border">
              <h3 className="text-sm font-medium text-muted-foreground mb-3">Quick Actions</h3>
              <div className="space-y-2">
                {quickActions.map((action) => (
                  <button
                    key={action.action}
                    onClick={() => handleQuickAction(action.action)}
                    className="
                      w-full flex items-center space-x-3 px-3 py-2 rounded-lg
                      text-sm text-muted-foreground
                      hover:bg-muted hover:text-foreground
                      transition-all duration-200 ease-out
                      focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2
                    "
                  >
                    <Icon name={action.icon} size={16} />
                    <span>{action.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Portfolio Summary */}
          {!isCollapsed && (
            <div className="p-4 border-t border-border bg-muted/30">
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Portfolio Value</span>
                  <span className="text-sm font-mono font-semibold text-foreground">₹10,434,430.50</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Today's Change</span>
                  <span className="text-sm font-mono font-semibold text-success">+₹194,340.25</span>
                </div>
                <div className="w-full bg-muted rounded-full h-2 mt-2">
                  <div className="bg-success h-2 rounded-full" style={{ width: '68%' }}></div>
                </div>
                <div className="text-xs text-muted-foreground text-center">68% of target</div>
              </div>
            </div>
          )}
        </div>
      </aside>

      {/* Main Content Spacer */}
      <div className={`
        hidden lg:block transition-all duration-300 ease-out
        ${isCollapsed ? 'w-16' : 'w-64'}
      `} />
    </>
  );
};

export default Sidebar;